/**
 * Bubble Me Side Cart - High Performance Theme Cart Enhancer
 * Optimized for 60fps animations, zero redundant network calls, and instant UI syncing.
 */

(function () {
  'use strict';

  const settings = window.__BUBBLE_CART_SETTINGS__ || {};
  let inFlightFetch = null;
  let lastFetchTime = 0;
  let cachedCart = null;
  let debounceTimer = null;
  let giftReconcilePromise = null;
  let couponApplyPromise = null;

  const cartRoot = (path) => `${window.Shopify?.routes?.root || '/'}${path}`.replace(/\/+/g, '/');
  const isGiftLine = (item) => String(item?.properties?._gift || '').toLowerCase() === 'true';

  function getMerchandiseSubtotal(cart) {
    return (cart?.items || []).reduce((total, item) => {
      if (isGiftLine(item)) return total;
      const linePrice = item.original_line_price ?? item.final_line_price ?? item.line_price ?? 0;
      return total + Number(linePrice || 0);
    }, 0) / 100;
  }

  function getDrawerElement() {
    return document.querySelector('#cart-drawer, cart-drawer, .drawer--cart, #CartDrawer, [data-cart-drawer], .mini-cart');
  }

  function getRewardsBarElement() {
    return document.querySelector('.cart-rewards-bar');
  }

  // Dynamically resolve active tiers from DOM elements, settings, or fallback
  function getActiveTiers(bar) {
    if (bar) {
      const nodeEls = Array.from(bar.querySelectorAll('.cart-rewards-bar__node[data-threshold]'));
      if (nodeEls.length > 0) {
        return nodeEls.map((el, idx) => ({
          index: idx + 1,
          threshold: parseFloat(el.dataset.threshold) || 0,
          title: el.dataset.title || `Reward ${idx + 1}`,
          type: el.dataset.type || 'gift',
          element: el
        })).sort((a, b) => a.threshold - b.threshold);
      }
    }
    if (Array.isArray(settings.rewardsTiers) && settings.rewardsTiers.length > 0) {
      return settings.rewardsTiers.map((t, idx) => ({
        index: idx + 1,
        threshold: parseFloat(t.threshold) || 0,
        title: t.title || t.name || `Reward ${idx + 1}`,
        type: t.type || 'gift',
        variantId: t.variant_id || t.variantId,
        product: t.product
      })).sort((a, b) => a.threshold - b.threshold);
    }
    const t1 = parseFloat(settings.freeShippingThreshold) || 499;
    const t2 = parseFloat(settings.gift1Threshold) || 799;
    const t3 = parseFloat(settings.gift2Threshold) || 1899;
    const list = [
      { index: 1, threshold: t1, title: settings.tier1Text || 'Free Shipping', type: 'shipping' }
    ];
    if (t2 > 0 && t2 !== t1) {
      list.push({ index: 2, threshold: t2, title: settings.gift1Text || 'Salt Sachet', type: 'gift' });
    }
    if (t3 > 0 && t3 !== t2) {
      list.push({ index: 3, threshold: t3, title: settings.gift2Text || 'Body Oil', type: 'gift' });
    }
    return list;
  }

  // Synchronize rewards bar progress, status message, and milestone states dynamically
  function syncRewardsBar(cart) {
    if (!cart) return;
    const bar = getRewardsBarElement();
    if (!bar) return;

    const subtotal = getMerchandiseSubtotal(cart);
    const tiers = getActiveTiers(bar);
    if (!tiers || tiers.length === 0) return;

    const tierCount = tiers.length;
    bar.style.setProperty('--tier-count', String(tierCount));
    const segmentPct = 100 / tierCount;

    let pct = 0;
    let message = '';

    if (subtotal <= 0) {
      pct = 0;
      message = `You are ₹${Math.round(tiers[0].threshold)} away from ${tiers[0].title}!`;
    } else if (subtotal < tiers[0].threshold) {
      pct = (subtotal / tiers[0].threshold) * segmentPct;
      const diff = Math.round(tiers[0].threshold - subtotal);
      message = `You are ₹${diff} away from ${tiers[0].title}!`;
    } else {
      let currentTierIdx = 0;
      for (let i = 0; i < tierCount; i++) {
        if (subtotal >= tiers[i].threshold) {
          currentTierIdx = i;
        }
      }

      if (currentTierIdx >= tierCount - 1) {
        pct = 100;
        message = '🎉 Congratulations! You unlocked all free gifts & shipping!';
      } else {
        const currentTier = tiers[currentTierIdx];
        const nextTier = tiers[currentTierIdx + 1];
        const segRange = nextTier.threshold - currentTier.threshold;
        const segVal = subtotal - currentTier.threshold;
        const segProgress = segRange > 0 ? (segVal / segRange) * segmentPct : 0;
        pct = ((currentTierIdx + 1) * segmentPct) + segProgress;
        const diff = Math.round(nextTier.threshold - subtotal);
        message = `${currentTier.title} unlocked! Add ₹${diff} for ${nextTier.title}!`;
      }
    }

    if (pct > 100) pct = 100;

    const fill = bar.querySelector('.cart-rewards-bar__fill');
    if (fill) fill.style.width = `${pct}%`;

    const textEl = bar.querySelector('.cart-rewards-bar__text');
    if (textEl) textEl.textContent = message;

    tiers.forEach((t) => {
      const node = t.element || bar.querySelector(`.cart-rewards-bar__node[data-tier="${t.index}"]`);
      if (node) node.classList.toggle('is-unlocked', subtotal >= t.threshold);
    });
  }

  function getGiftMilestones() {
    if (Array.isArray(settings.rewardsTiers) && settings.rewardsTiers.length > 0) {
      const dynamicGifts = [];
      settings.rewardsTiers.forEach((t, i) => {
        const variantId = Number(t.variant_id || t.variantId);
        if ((t.type === 'gift' || variantId > 0) && variantId > 0) {
          dynamicGifts.push({
            id: `gift-${t.id || i + 1}`,
            threshold: Number(t.threshold) || 0,
            variantId: variantId,
            ready: true
          });
        }
      });
      if (dynamicGifts.length > 0) return dynamicGifts;
    }
    return [
      { id: 'gift-1', threshold: Number(settings.gift1Threshold) || 799, variantId: Number(settings.gift1VariantId), ready: settings.gift1Ready === true },
      { id: 'gift-2', threshold: Number(settings.gift2Threshold) || 1899, variantId: Number(settings.gift2VariantId), ready: settings.gift2Ready === true }
    ];
  }

  async function changeGiftLine(lineKey, quantity) {
    const response = await fetch(cartRoot('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Bubble-Internal': 'true' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!response.ok) throw new Error(`Gift cart change failed (${response.status})`);
  }

  async function reconcileGiftLines(cart) {
    if (giftReconcilePromise) return giftReconcilePromise;
    giftReconcilePromise = (async () => {
      const subtotal = getMerchandiseSubtotal(cart);
      const desired = new Map(
        getGiftMilestones()
          .filter((gift) => gift.ready && gift.variantId > 0 && subtotal >= gift.threshold)
          .map((gift) => [gift.id, gift])
      );
      const giftLines = (cart.items || []).filter(isGiftLine);
      const seen = new Set();
      let changed = false;

      for (const line of giftLines) {
        const milestone = String(line.properties?._milestone || '');
        const expected = desired.get(milestone);
        const keep = expected && Number(line.variant_id || line.id) === expected.variantId && !seen.has(milestone);
        if (!keep) {
          await changeGiftLine(line.key, 0);
          changed = true;
        } else {
          seen.add(milestone);
          if (line.quantity !== 1) {
            await changeGiftLine(line.key, 1);
            changed = true;
          }
        }
      }

      for (const [milestone, gift] of desired) {
        if (seen.has(milestone)) continue;
        const response = await fetch(cartRoot('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Bubble-Internal': 'true' },
          body: JSON.stringify({
            items: [{ id: gift.variantId, quantity: 1, properties: { _gift: 'true', _milestone: milestone } }]
          })
        });
        if (!response.ok) {
          console.warn('[Bubble Me] Gift unavailable', milestone, response.status);
          continue;
        }
        changed = true;
      }

      if (!changed) return cart;
      cachedCart = null;
      lastFetchTime = 0;
      const confirmed = await fetchCartData(true);
      if (confirmed) document.dispatchEvent(new CustomEvent('bubbleme:gifts-reconciled', { detail: { cart: confirmed } }));
      return confirmed || cart;
    })().finally(() => { giftReconcilePromise = null; });
    return giftReconcilePromise;
  }

  // Get cart data with in-flight deduplication and 800ms cache
  async function fetchCartData(force = false) {
    const now = Date.now();
    if (!force && cachedCart && now - lastFetchTime < 800) {
      return cachedCart;
    }
    if (inFlightFetch) {
      return inFlightFetch;
    }

    inFlightFetch = fetch('/cart.js')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        cachedCart = data;
        lastFetchTime = Date.now();
        inFlightFetch = null;
        return data;
      })
      .catch(() => {
        inFlightFetch = null;
        return null;
      });

    return inFlightFetch;
  }

  // Active Store Coupons from User Offers
  const ACTIVE_COUPONS = [
    { code: 'UZMJZZCQGCBF', savings: 499, minSubtotal: 499, label: 'Saved ₹499 with UZMJZZCQGCBF' },
    { code: 'D7UVT3JBMYDU', savings: 110, minSubtotal: 0, label: 'Saved ₹110 with D7UVT3JBMYDU' }
  ];

  // Read discount codes from every shape returned by Shopify's Ajax Cart API.
  function getVerifiedCouponState(cart) {
    if (!cart) return { codes: [], activeCode: '' };
    const codes = [];
    const addCode = (value) => {
      const code = String(value || '').trim().toUpperCase();
      if (code && !codes.includes(code)) codes.push(code);
    };
    (cart.discount_codes || []).forEach((discount) => {
      if (discount && discount.applicable !== false) addCode(discount.code);
    });
    (cart.cart_level_discount_applications || []).forEach((discount) => {
      if (!discount || discount.type === 'discount_code') addCode(discount && discount.title);
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const application = allocation && allocation.discount_application;
        if (!application || application.type === 'discount_code') addCode(application && application.title);
      });
    });
    return { codes, activeCode: codes[0] || '' };
  }

  // Propagate only a coupon already verified in Shopify's cart response.
  function syncVerifiedCouponToCheckout(couponCode) {
    if (!couponCode) return;
    const code = couponCode.trim().toUpperCase();
    try {
      sessionStorage.setItem('bm_last_verified_coupon', code);
    } catch (_) {}
    document.querySelectorAll('a[href*="/checkout"], form[action*="/checkout"]').forEach((el) => {
      if (el.tagName === 'A') {
        try {
          const url = new URL(el.href, window.location.origin);
          url.searchParams.set('discount', code);
          el.href = url.toString();
        } catch (_) {}
      } else if (el.tagName === 'FORM') {
        try {
          const url = new URL(el.action, window.location.origin);
          url.searchParams.set('discount', code);
          el.action = url.toString();
        } catch (_) {}
      }
    });

    const checkoutBtn = document.querySelector('#checkout, .cart__checkout-button');
    if (checkoutBtn && checkoutBtn.form) {
      let discInput = checkoutBtn.form.querySelector('input[name="discount"]');
      if (!discInput) {
        discInput = document.createElement('input');
        discInput.type = 'hidden';
        discInput.name = 'discount';
        checkoutBtn.form.appendChild(discInput);
      }
      discInput.value = code;
    }
    document.dispatchEvent(new CustomEvent('bubbleme:coupon-verified', { detail: { code } }));
  }

  async function applyAndVerifyCoupon(rawCode) {
    const code = String(rawCode || '').trim().toUpperCase();
    if (!/^[A-Z0-9_-]+$/.test(code)) return { applied: false, cart: cachedCart, code: '' };
    if (couponApplyPromise) return couponApplyPromise;
    couponApplyPromise = (async () => {
      try { sessionStorage.setItem('bm_last_attempted_coupon', code); } catch (_) {}
      const response = await fetch(cartRoot('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code })
      });
      if (!response.ok) return { applied: false, cart: cachedCart, code };
      cachedCart = null;
      lastFetchTime = 0;
      const confirmedCart = await fetchCartData(true);
      const verified = getVerifiedCouponState(confirmedCart);
      if (verified.codes.includes(code)) syncVerifiedCouponToCheckout(code);
      return { applied: verified.codes.includes(code), cart: confirmedCart, code };
    })().finally(() => { couponApplyPromise = null; });
    return couponApplyPromise;
  }

  // Determine the best auto-apply coupon code based on subtotal
  function getBestAutoCoupon(subtotalRupees) {
    if (settings.autoCouponCode && settings.autoCouponCode !== 'UZMJZZCQGCBF' && settings.autoCouponCode !== 'D7UVT3JBMYDU' && settings.autoCouponCode !== 'BUBBLE10') {
      return settings.autoCouponCode.toUpperCase();
    }
    if (subtotalRupees >= 1499) {
      return 'UZMJZZCQGCBF';
    }
    return 'D7UVT3JBMYDU';
  }

  // Determine all unlocked coupons based on cart subtotal & freebie conditions
  function getEligibleCoupons(cart) {
    if (!cart) return [];
    const subtotal = getMerchandiseSubtotal(cart);
    const verifiedCodes = getVerifiedCouponState(cart).codes;
    const codes = [];

    // Include any discount code already verified directly in the Shopify cart
    verifiedCodes.forEach((c) => {
      const code = String(c || '').trim().toUpperCase();
      if (code && !codes.includes(code)) codes.push(code);
    });

    const hasFreebies = (cart.items || []).some(isGiftLine) || subtotal >= (settings.gift1Threshold || 799);

    // Condition 1: Subtotal >= ₹499 -> Base coupon D7UVT3JBMYDU
    const condition1Met = subtotal >= (settings.freeShippingThreshold || 499);
    // Condition 2: Subtotal >= ₹1499 (or freebies unlocked) -> High tier coupon UZMJZZCQGCBF
    const condition2Met = subtotal >= 1499 || hasFreebies;

    const baseCoupon = 'D7UVT3JBMYDU';
    const primaryCoupon = (settings.autoCouponCode && !['UZMJZZCQGCBF', 'D7UVT3JBMYDU', 'BUBBLE10'].includes(settings.autoCouponCode))
      ? settings.autoCouponCode.toUpperCase()
      : 'UZMJZZCQGCBF';

    if (condition2Met) {
      if (!codes.includes(primaryCoupon)) codes.push(primaryCoupon);
      // When both conditions are met, include both coupons in the tab
      if (condition1Met && !codes.includes(baseCoupon)) {
        codes.push(baseCoupon);
      }
    } else if (condition1Met) {
      if (!codes.includes(baseCoupon)) codes.push(baseCoupon);
    }

    return codes.filter((c) => {
      try {
        return sessionStorage.getItem(`bm_dismissed_${c}`) !== 'true';
      } catch (_) {
        return true;
      }
    });
  }

  // Synchronize the Discount Section input & buttons
  function syncDiscountSectionUI(cart) {
    if (!cart) return;
    const drawer = getDrawerElement();
    if (!drawer) return;

    // Keep subtotal text red
    const totalEl = drawer.querySelector('.cart-totals__total-value, [data-cart-subtotal]');
    if (totalEl) totalEl.style.setProperty('color', '#c5221f', 'important');

    const subtotal = getMerchandiseSubtotal(cart);
    const discountInput = drawer.querySelector('#cart-discount, input[name="discount"]');
    const applyButton = drawer.querySelector('.cart-discount__button');
    if (!discountInput || !applyButton) return;

    const inputRow = discountInput.closest('.cart-discount__input-row');
    const eligibleCodes = getEligibleCoupons(cart);
    const hasAppliedCoupons = eligibleCodes.length > 0 && subtotal > 0;

    // Hide legacy codes list if present
    const legacyCodesList = drawer.querySelector('.cart-discount__codes');
    if (legacyCodesList) {
      legacyCodesList.style.setProperty('display', 'none', 'important');
    }

    // Ensure Capsule element exists inside .cart-discount__input-row
    let capsule = inputRow ? inputRow.querySelector('.cart-discount__applied-capsule') : null;
    if (inputRow && !capsule) {
      capsule = document.createElement('div');
      capsule.id = 'cart-discount-applied-capsule';
      capsule.className = 'cart-discount__applied-capsule hidden';
      capsule.innerHTML = `
        <div class="cart-discount__applied-chips-list"></div>
        <div class="cart-discount__max-notice">
          <span class="cart-discount__max-icon" aria-hidden="true">✓</span>
          <span class="cart-discount__max-label">Maximum<br>codes<br>applied</span>
        </div>
      `;
      inputRow.insertBefore(capsule, discountInput);
    }

    if (hasAppliedCoupons) {
      discountInput.value = '';
      discountInput.classList.add('hidden');
      discountInput.style.setProperty('display', 'none', 'important');

      if (capsule) {
        capsule.classList.remove('hidden');
        capsule.style.setProperty('display', 'flex', 'important');

        const chipsList = capsule.querySelector('.cart-discount__applied-chips-list');
        if (chipsList) {
          chipsList.innerHTML = '';
          eligibleCodes.forEach((code) => {
            const chip = document.createElement('div');
            chip.className = 'cart-discount__chip';
            chip.dataset.code = code;
            chip.innerHTML = `
              <span class="cart-discount__chip-check-wrapper" aria-hidden="true">
                <svg width="8" height="8" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6l3 3 5-5"/></svg>
              </span>
              <span class="cart-discount__chip-text">${code}</span>
              <button type="button" class="cart-discount__chip-remove button-unstyled" aria-label="Remove discount ${code}" title="Remove ${code}">
                <svg width="9" height="9" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l12 12M13 1L1 13"/></svg>
              </button>
            `;
            const removeBtn = chip.querySelector('.cart-discount__chip-remove');
            if (removeBtn) {
              removeBtn.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                try { sessionStorage.setItem(`bm_dismissed_${code}`, 'true'); } catch (_) {}
                chip.remove();
                await fetch(cartRoot('cart/update.js'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ discount: '' })
                });
                cachedCart = null;
                const updated = await fetchCartData(true);
                syncDiscountSectionUI(updated);
              });
            }
            chipsList.appendChild(chip);
          });
        }
      }

      applyButton.classList.add('is-applied');
      applyButton.innerHTML = `
        <svg class="cart-discount__button-check" width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
        </svg>
        <span>Applied</span>
      `;
    } else {
      if (subtotal <= 0) discountInput.value = '';
      discountInput.classList.remove('hidden');
      discountInput.style.removeProperty('display');

      if (capsule) {
        capsule.classList.add('hidden');
        capsule.style.setProperty('display', 'none', 'important');
      }

      applyButton.classList.remove('is-applied');
      applyButton.innerHTML = '<span>Apply</span>';
    }

    // Ensure Secure Transaction badge exists in .cart__ctas right under checkout button
    const checkoutBtn = drawer.querySelector('#checkout, .cart__checkout-button');
    if (checkoutBtn && !drawer.querySelector('.cart__ctas .cart-drawer__secure-badge')) {
      const badge = document.createElement('div');
      badge.className = 'cart-drawer__secure-badge';
      badge.setAttribute('aria-label', 'Secure Transaction');
      badge.innerHTML = `
        <svg class="cart-drawer__shield-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="m9 12 2 2 4-4"/>
        </svg>
        <span>Secure Transaction</span>
      `;
      checkoutBtn.insertAdjacentElement('afterend', badge);
    }

    // Bind input watcher only once
    if (!discountInput.dataset.inputWatchBound) {
      discountInput.dataset.inputWatchBound = 'true';
      discountInput.addEventListener('input', () => {
        const val = discountInput.value.trim().toUpperCase();
        if (val && !eligibleCodes.includes(val)) {
          applyButton.classList.remove('is-applied');
          applyButton.innerHTML = '<span>Apply</span>';
        } else if (hasAppliedCoupons) {
          applyButton.classList.add('is-applied');
          applyButton.innerHTML = `
            <svg class="cart-discount__button-check" width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
            </svg>
            <span>Applied</span>
          `;
        }
      });
    }

    if (!applyButton.dataset.bubblemeApplyBound) {
      applyButton.dataset.bubblemeApplyBound = 'true';
      applyButton.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        // If currently in applied state, clicking allows customer to enter a new code
        if (applyButton.classList.contains('is-applied')) {
          if (discountInput.style.display === 'none' || discountInput.classList.contains('hidden')) {
            if (capsule) {
              capsule.classList.add('hidden');
              capsule.style.setProperty('display', 'none', 'important');
            }
            discountInput.classList.remove('hidden');
            discountInput.style.removeProperty('display');
            discountInput.focus();
            return;
          }
        }

        const enteredVal = discountInput.value.trim().toUpperCase();
        if (!enteredVal) return;
        applyButton.disabled = true;
        applyButton.textContent = 'Applying…';
        const result = await applyAndVerifyCoupon(enteredVal);
        if (result.cart) {
          discountInput.value = '';
          syncDiscountSectionUI(result.cart);
        }
        applyButton.disabled = false;
        if (!result.applied) {
          applyButton.classList.remove('is-applied');
          applyButton.innerHTML = '<span>Try again</span>';
        }
      }, { capture: true });
    }
  }

  let lastCouponAttempt = { code: '', at: 0 };

  // Auto-apply discount coupon as conditions are fulfilled & push to payment gateways
  async function checkCartState(passedCart) {
    try {
      let cart = passedCart || await fetchCartData();
      if (!cart) return;

      cart = await reconcileGiftLines(cart);
      syncRewardsBar(cart);
      syncDiscountSectionUI(cart);

      if (getMerchandiseSubtotal(cart) <= 0) return;
      if (!settings.enableAutoCoupon) return;

      const subtotalRupees = getMerchandiseSubtotal(cart);
      const eligibleCodes = getEligibleCoupons(cart);
      const targetCoupon = eligibleCodes[0] || getBestAutoCoupon(subtotalRupees);
      const verifiedCoupon = getVerifiedCouponState(cart);
      const existingDiscounts = verifiedCoupon.codes;

      // When freebies or conditions are met, ensure all eligible coupons are synced to checkout
      if (eligibleCodes.length > 0) {
        syncVerifiedCouponToCheckout(eligibleCodes.join(','));
      }

      // If already applied in Shopify cart, push to gateways and exit
      if (existingDiscounts.includes(targetCoupon)) {
        syncVerifiedCouponToCheckout(eligibleCodes.length > 0 ? eligibleCodes.join(',') : targetCoupon);
        return;
      }

      // If customer manually selected another valid discount, respect it and push
      if (existingDiscounts.length > 0) {
        syncVerifiedCouponToCheckout(existingDiscounts[0]);
        return;
      }

      if (lastCouponAttempt.code === targetCoupon && Date.now() - lastCouponAttempt.at < 5000) return;
      lastCouponAttempt = { code: targetCoupon, at: Date.now() };
      const result = await applyAndVerifyCoupon(targetCoupon);
      if (result.cart) syncDiscountSectionUI(result.cart);
    } catch (error) {
      console.warn('[Bubble Me] Cart reconciliation failed', error);
    }
  }


  // Intercept checkout clicks (both GoKwik and Shopify native checkout) to push coupon to gateway
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#checkout, .cart__checkout-button, button[name="checkout"], [data-gokwik-function], .gokwik-marge');
    if (btn) {
      const eligibleCodes = getEligibleCoupons(cachedCart);
      const codeToSync = eligibleCodes.length > 0 ? eligibleCodes.join(',') : getVerifiedCouponState(cachedCart).activeCode;
      if (codeToSync) syncVerifiedCouponToCheckout(codeToSync);
    }
  }, { capture: true });

  // Debounced trigger to coalesce rapid events into a single execution
  function scheduleCartCheck(e) {
    const passedCart = e && e.detail && e.detail.cart ? e.detail.cart : null;
    if (passedCart) {
      cachedCart = passedCart;
      lastFetchTime = Date.now();
      checkCartState(passedCart);
      return;
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      checkCartState();
    }, 60);
  }

  // Initialize once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => checkCartState(), { passive: true });
  } else {
    checkCartState();
  }

  // Lightweight passive event listeners
  const events = [
    'cart:open',
    'shopify:cart:lines-update',
    'cart:lines-update',
    'theme-drawer:open',
    'cart:item-added',
    'cart:add',
    'cart:updated',
    'bubble:cart-updated'
  ];
  events.forEach((evt) => {
    document.addEventListener(evt, scheduleCartCheck, { passive: true });
  });

  // Intercept window.fetch for /cart/* operations to guarantee zero missed updates
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
        const isInternal = args[1]?.headers?.['X-Bubble-Internal'] || (args[1]?.headers instanceof Headers && args[1]?.headers.get('X-Bubble-Internal'));
        if (!isInternal) {
          scheduleCartCheck();
        }
      }
    } catch (_) {}
    return response;
  };
})();
